import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Fab, Paper, Slide, IconButton, Box, Tooltip } from '@mui/material';
import { Chat as ChatIcon, Close as CloseIcon, DragIndicator as DragIcon, OpenInFull, CloseFullscreen } from '@mui/icons-material';
import { useWebSocket, useChatSession } from './hooks';
import { MessageList, MessageInput, ConnectionStatus } from './components';
import { CHAT_API_BASE_URL_READ, getChatAccessToken } from './config';
import type { ChatMessage, ChatMessageType } from './types';
import { ConsentConfirmationModal } from '../PrivacyPolicySetting';
import { useModal } from '../PrivacyPolicySetting/hooks/useModal';
import { showWarningSnackbar } from '../../components/snackBar/snakbar';
import './FloatingChatWidget.css';
import CustomTooltip from '../../components/CustomTooltip';

type FloatingChatWidgetProps = {
  isProd: boolean;
};

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const MIN_WIDGET_WIDTH = 320;
const MIN_WIDGET_HEIGHT = 360;

export default function FloatingChatWidget({ isProd }: FloatingChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 470, y: 20 });
  const [size, setSize] = useState({ width: 400, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({
    direction: 'se' as ResizeDirection,
    mouseX: 0,
    mouseY: 0,
    x: 0,
    y: 0,
    width: 400,
    height: 600,
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);
  const preMaximizeStateRef = useRef({ position: { x: window.innerWidth - 470, y: 20 }, size: { width: 400, height: 600 } });

  const { sessionId, loading: sessionLoading } = useChatSession();
  const { connect, sendMessage, isConnected, connectionError } = useWebSocket(sessionId);
  const connectedSessionIdRef = useRef<string | null>(null);
  const hasConnectedRef = useRef<boolean>(false);
  const pendingWidgetOpenRef = useRef<'main' | 'chat'>('main');

  const openWidgetAfterConsent = useCallback(() => {
    if (pendingWidgetOpenRef.current === 'chat') {
      setIsChatOpen(true);
      setIsMinimized(false);
      return;
    }
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const { open: aiConsentModalOpen, closeModal: closeAiConsentModal, handleAiButtonClick, pendingAiConsentRef } = useModal({
    onAccepted: openWidgetAfterConsent,
  });

  const [fabPosition, setFabPosition] = useState({
    x: window.innerWidth - 50,
    y: window.innerHeight - 200,
  });
  const [isFabDragging, setIsFabDragging] = useState(false);
  const [fabDragStart, setFabDragStart] = useState({ x: 0, y: 0 });
  const [hasMoved, setHasMoved] = useState(false);

  const handleFabMouseDown = (e: React.MouseEvent) => {
    setIsFabDragging(true);
    setHasMoved(false);
    setFabDragStart({
      x: e.clientX - fabPosition.x,
      y: e.clientY - fabPosition.y,
    });
  };

  const handleToggleOne = () => {
    if (isChatOpen) {
      setIsChatOpen(false);
      return;
    }
    pendingWidgetOpenRef.current = 'chat';
    handleAiButtonClick();
  };

  const handleFabClick = () => {
    if (hasMoved) return; // prevent click when dragging
    handleToggleOne(); // or handleToggle based on your condition
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isFabDragging) return;

      const newX = e.clientX - fabDragStart.x;
      const newY = e.clientY - fabDragStart.y;

      // detect movement (to prevent click trigger)
      if (Math.abs(e.clientX - fabDragStart.x) > 5 || Math.abs(e.clientY - fabDragStart.y) > 5) {
        setHasMoved(true);
      }

      setFabPosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - 60)),
        y: Math.max(0, Math.min(newY, window.innerHeight - 60)),
      });
    };

    const handleMouseUp = () => {
      setIsFabDragging(false);
      setTimeout(() => setHasMoved(false), 0);
    };

    if (isFabDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isFabDragging, fabDragStart]);

  const loadMessagesFromSession = useCallback(async (sessionIdToLoad: string) => {
    const token = getChatAccessToken();
    if (!token) return;
    try {
      const response = await fetch(`${CHAT_API_BASE_URL_READ}/chat/session/${sessionIdToLoad}/messages?limit=50&offset=0`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const loadedMessages = (data.messages ?? [])
          .reverse()
          .filter((msg: { role?: string }) => msg.role !== 'system')
          .map(
            (msg: { id?: unknown; message_id?: unknown; role: string; content?: string; created_at?: string; message_type?: string }) => ({
              id: String(msg.id ?? msg.message_id ?? ''),
              role: msg.role as 'user' | 'assistant',
              content: msg.content || '',
              timestamp: new Date(msg.created_at || Date.now()),
              message_type: (msg.message_type === 'greeting'
                ? 'greeting'
                : msg.message_type === 'download'
                  ? 'download'
                  : 'text') as ChatMessageType,
              download_url: (msg as { download_url?: string }).download_url,
            })
          );
        setMessages(loadedMessages);
      }
    } catch (err) {
      console.error('[FloatingChatWidget] Failed to load messages:', err);
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      loadMessagesFromSession(sessionId);
    } else {
      setMessages([]);
    }
  }, [sessionId, loadMessagesFromSession]);

  useEffect(() => {
    if (sessionId && !sessionLoading) {
      if (connectedSessionIdRef.current !== sessionId && !hasConnectedRef.current) {
        connectedSessionIdRef.current = sessionId;
        hasConnectedRef.current = true;
        const timeoutId = setTimeout(() => connect(sessionId), 100);
        return () => clearTimeout(timeoutId);
      } else if (connectedSessionIdRef.current !== sessionId) {
        connectedSessionIdRef.current = sessionId;
      }
    } else if (!sessionId) {
      connectedSessionIdRef.current = null;
      hasConnectedRef.current = false;
    }
  }, [sessionId, sessionLoading, connect]);

  useEffect(() => {
    if (connectionError) setIsLoading(false);
  }, [connectionError]);

  useEffect(() => {
    const handleChatMessage = (event: Event) => {
      const customEvent = event as CustomEvent<{
        type: string;
        sender?: string;
        message?: string;
        timestamp?: string;
        message_type?: string;
        download_url?: string;
        download_urls?: string[];
        balance_suggestions?: ChatMessage['balance_suggestions'];
      }>;
      const data = customEvent.detail;
      if (data.type === 'message' && data.sender === 'bot' && data.message !== undefined) {
        setIsLoading(false);
        const botMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(data.timestamp || Date.now()),
          message_type: (data.message_type === 'greeting'
            ? 'greeting'
            : data.message_type === 'download'
              ? 'download'
              : 'text') as ChatMessageType,
          download_url: data.download_url,
          download_urls: data.download_urls,
          balance_suggestions: data.balance_suggestions,
        };
        setMessages((prev) => [...prev, botMessage]);
      }
    };

    const handleSystemMessage = (event: Event) => {
      const customEvent = event as CustomEvent<{ type: string; message?: string; content?: string; timestamp?: string }>;
      const data = customEvent.detail;
      if (data.type === 'system' || data.type === 'connection') {
        const systemMessage: ChatMessage = {
          id: Date.now().toString(),
          role: 'system',
          content: data.message || data.content || 'Connected',
          timestamp: new Date(data.timestamp || Date.now()),
        };
        setMessages((prev) => [...prev, systemMessage]);
      }
    };

    window.addEventListener('chat-message', handleChatMessage);
    window.addEventListener('system-message', handleSystemMessage);
    return () => {
      window.removeEventListener('chat-message', handleChatMessage);
      window.removeEventListener('system-message', handleSystemMessage);
    };
  }, []);

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || !isConnected || !sessionId) return;
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      sendMessage(content.trim());
    },
    [isConnected, sessionId, sendMessage]
  );

  const handleToggle = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    pendingWidgetOpenRef.current = 'main';
    handleAiButtonClick();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isMaximized) return;
    if (e.target === dragRef.current || dragRef.current?.contains(e.target as Node)) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging && !isResizing) {
      const widgetWidth = chatRef.current?.getBoundingClientRect().width || 400;
      const widgetHeight = chatRef.current?.getBoundingClientRect().height || (isMinimized ? 60 : 600);
      const maxX = window.innerWidth - widgetWidth;
      const minYFromBottom = window.innerHeight - 70;
      const maxY = minYFromBottom - widgetHeight;
      setPosition({
        x: Math.max(0, Math.min(e.clientX - dragStart.x, maxX)),
        y: Math.max(0, Math.min(e.clientY - dragStart.y, maxY)),
      });
    }
  };

  const handleResizeMouseDown = (direction: ResizeDirection, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isMinimized || isMaximized) return;
    setIsResizing(true);
    setResizeStart({
      direction,
      mouseX: e.clientX,
      mouseY: e.clientY,
      x: position.x,
      y: position.y,
      width: chatRef.current?.getBoundingClientRect().width || size.width,
      height: chatRef.current?.getBoundingClientRect().height || size.height,
    });
  };

  const handleResizeMouseMove = (e: MouseEvent) => {
    if (!isResizing || isMinimized) return;

    const dx = e.clientX - resizeStart.mouseX;
    const dy = e.clientY - resizeStart.mouseY;
    const maxBottom = window.innerHeight - 70;

    let nextX = resizeStart.x;
    let nextY = resizeStart.y;
    let nextWidth = resizeStart.width;
    let nextHeight = resizeStart.height;

    if (resizeStart.direction.includes('e')) nextWidth = resizeStart.width + dx;
    if (resizeStart.direction.includes('w')) {
      nextWidth = resizeStart.width - dx;
      nextX = resizeStart.x + dx;
    }
    if (resizeStart.direction.includes('s')) nextHeight = resizeStart.height + dy;
    if (resizeStart.direction.includes('n')) {
      nextHeight = resizeStart.height - dy;
      nextY = resizeStart.y + dy;
    }

    if (nextWidth < MIN_WIDGET_WIDTH) {
      if (resizeStart.direction.includes('w')) {
        nextX -= MIN_WIDGET_WIDTH - nextWidth;
      }
      nextWidth = MIN_WIDGET_WIDTH;
    }
    if (nextHeight < MIN_WIDGET_HEIGHT) {
      if (resizeStart.direction.includes('n')) {
        nextY -= MIN_WIDGET_HEIGHT - nextHeight;
      }
      nextHeight = MIN_WIDGET_HEIGHT;
    }

    if (nextX < 0) {
      nextWidth += nextX;
      nextX = 0;
    }
    if (nextY < 0) {
      nextHeight += nextY;
      nextY = 0;
    }

    if (nextX + nextWidth > window.innerWidth) {
      nextWidth = window.innerWidth - nextX;
    }
    if (nextY + nextHeight > maxBottom) {
      nextHeight = maxBottom - nextY;
    }

    nextWidth = Math.max(MIN_WIDGET_WIDTH, nextWidth);
    nextHeight = Math.max(MIN_WIDGET_HEIGHT, nextHeight);

    setPosition({ x: nextX, y: nextY });
    setSize({ width: nextWidth, height: nextHeight });
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleResizeMouseUp = () => setIsResizing(false);

  const handleToggleMaximize = () => {
    if (!isMaximized) {
      preMaximizeStateRef.current = { position: { ...position }, size: { ...size } };
      setIsMinimized(false);
      setIsMaximized(true);
      setPosition({ x: 0, y: 0 });
      setSize({ width: window.innerWidth, height: window.innerHeight });
    } else {
      setIsMaximized(false);
      setPosition(preMaximizeStateRef.current.position);
      setSize(preMaximizeStateRef.current.size);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleResizeMouseUp);
      };
    }
  }, [isResizing, resizeStart, isMinimized]);

  useEffect(() => {
    const handleResize = () => {
      if (isMaximized) {
        setPosition({ x: 0, y: 0 });
        setSize({ width: window.innerWidth, height: window.innerHeight });
        return;
      }
      const widgetWidth = chatRef.current?.getBoundingClientRect().width || size.width;
      const widgetHeight = chatRef.current?.getBoundingClientRect().height || (isMinimized ? 60 : size.height);
      const maxX = window.innerWidth - widgetWidth;
      const minYFromBottom = window.innerHeight - 70;
      const maxY = minYFromBottom - widgetHeight;
      setPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }));

      setSize((prev) => ({
        width: Math.min(prev.width, window.innerWidth),
        height: Math.min(prev.height, Math.max(MIN_WIDGET_HEIGHT, window.innerHeight - 70)),
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMinimized, size.width, size.height, isMaximized]);

  // Keep widget in-bounds after manual resize.
  useEffect(() => {
    if (!chatRef.current || !isOpen || isMinimized) return;
    const observer = new ResizeObserver(() => {
      const widgetWidth = chatRef.current?.getBoundingClientRect().width || 400;
      const widgetHeight = chatRef.current?.getBoundingClientRect().height || 600;
      const maxX = window.innerWidth - widgetWidth;
      const minYFromBottom = window.innerHeight - 70;
      const maxY = minYFromBottom - widgetHeight;
      setPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }));
    });
    observer.observe(chatRef.current);
    return () => observer.disconnect();
  }, [isOpen, isMinimized]);

  return (
    <>
      {isProd ? (
        <Tooltip
          title="AI Genie"
          placement="left"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#EE5B2B',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              },
            },
            arrow: {
              sx: {
                color: '#EE5B2B',
              },
            },
          }}
        >
          <Fab
            aria-label="chat"
            // onMouseDown={handleFabMouseDown}
            onClick={handleFabClick}
            sx={{
              width: 40,
              height: 40,
              minHeight: 30,
              position: 'fixed',
              top: 65,
              right: 10,
              zIndex: 1300,
              borderRadius: '50%',
              overflow: 'hidden',
              padding: '1px',

              '& img': {
                position: 'relative',
                zIndex: 2,
                width: '70%',
                height: '70%',
                objectFit: 'contain',
                mixBlendMode: 'multiply',
              },

              // 🔥 Single color animated border
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -3,
                borderRadius: '50%',
                background: `
        conic-gradient(
          rgba(238, 91, 43, 1),
          rgba(238, 91, 43, 0.6),
          rgba(238, 91, 43, 0.2),
          rgba(238, 91, 43, 0.6),
          rgba(238, 91, 43, 1)
        )
      `,
                animation: 'spinBorder 2s linear infinite',
                zIndex: 0,
              },

              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 3,
                borderRadius: '50%',
                background: '#fff',
                zIndex: 1,
              },

              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              '&:hover': {
                transform: 'scale(1.1)',
                transition: 'transform 0.2s ease',
              },

              '@keyframes spinBorder': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <img
              src="/LX_Genie_02 (1).gif"
              alt="chat"
              style={{
                width: '90%',
                height: '90%',
                objectFit: 'contain',
              }}
            />
          </Fab>
        </Tooltip>
      ) : (
        <Tooltip
          title="AI Genie"
          placement="left"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#EE5B2B',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 500,
                borderRadius: '8px',
                px: 1.5,
                py: 0.8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              },
            },
            arrow: {
              sx: {
                color: '#EE5B2B',
              },
            },
          }}
        >
          <Fab
            aria-label="chat"
            onClick={handleToggle}
            sx={{
              width: { xs: 25, md: 40 },
              height: { xs: 25, md: 40 },
              minHeight: { xs: 20, md: 30 },
              position: 'fixed',
              top: { xs: 45, md: 55 },
              right: 10,
              zIndex: 1300,
              borderRadius: '50%',
              overflow: 'hidden',
              padding: '1px',
              backgroundColor: 'transparent',

              '& img': {
                position: 'relative',
                zIndex: 2,
                width: '70%',
                height: '70%',
                objectFit: 'contain',
                mixBlendMode: 'multiply',
              },

              // 🔥 Single color animated border
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: -3,
                borderRadius: '50%',
                background: `
        conic-gradient(
          rgba(238, 91, 43, 1),
          rgba(238, 91, 43, 0.6),
          rgba(238, 91, 43, 0.2),
          rgba(238, 91, 43, 0.6),
          rgba(238, 91, 43, 1)
        )
      `,
                animation: 'spinBorder 2s linear infinite',
                zIndex: 0,
              },

              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 3,
                borderRadius: '50%',
                background: '#fff',
                zIndex: 1,
              },

              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              '&:hover': {
                transform: 'scale(1.1)',
                transition: 'transform 0.3s ease',
              },

              '@keyframes spinBorder': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <img
              src="/LX_Genie_02 (1).gif"
              alt="chat"
              style={{
                width: '90%',
                height: '90%',
                objectFit: 'contain',
              }}
            />
          </Fab>
        </Tooltip>
      )}

      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          ref={chatRef}
          elevation={8}
          sx={{
            position: 'fixed',
            left: isMaximized ? 0 : position.x,
            top: isMaximized ? 0 : position.y,
            width: isMaximized ? '100vw' : size.width,
            height: isMinimized ? 'auto' : isMaximized ? '100vh' : size.height,
            maxWidth: isMaximized ? '100vw' : { xs: '70vw', md: '99vw' },
            maxHeight: isMinimized ? 'none' : isMaximized ? '100vh' : { xs: '70vh', md: '99vh' },
            zIndex: 1301,
            borderRadius: isMaximized ? 0 : 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
          }}
          onMouseDown={handleMouseDown}
        >
          <Box
            ref={dragRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              cursor: 'move',
              minHeight: '48px',
              '&:hover': { backgroundColor: '#e8e8e8' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <DragIcon sx={{ color: '#666666', fontSize: 18, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ConnectionStatus isConnected={isConnected} error={connectionError} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(!isMinimized)}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#000000' } }}
              >
                {isMinimized ? '□' : '—'}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggleMaximize}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#000000' } }}
              >
                {isMaximized ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {!isMinimized && (
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                backgroundColor: '#efeae2',
              }}
            >
              {sessionLoading && !sessionId ? (
                <Box sx={{ p: 2, textAlign: 'center', color: '#666666' }}>Creating chat session...</Box>
              ) : (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 0,
                      maxHeight: '100%',
                      backgroundColor: '#efeae2',
                    }}
                  >
                    <MessageList messages={messages} isLoading={isLoading} onQuickActionClick={handleSendMessage} />
                  </Box>
                  <MessageInput onSendMessage={handleSendMessage} disabled={!isConnected || !sessionId} />
                </>
              )}
            </Box>
          )}

          {!isMinimized && !isMaximized && (
            <>
              <Box
                onMouseDown={(e) => handleResizeMouseDown('n', e)}
                sx={{ position: 'absolute', top: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('s', e)}
                sx={{ position: 'absolute', bottom: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('w', e)}
                sx={{ position: 'absolute', top: 8, bottom: 8, left: 0, width: 6, cursor: 'ew-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('e', e)}
                sx={{ position: 'absolute', top: 8, bottom: 8, right: 0, width: 6, cursor: 'ew-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('nw', e)}
                sx={{ position: 'absolute', top: 0, left: 0, width: 10, height: 10, cursor: 'nwse-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('ne', e)}
                sx={{ position: 'absolute', top: 0, right: 0, width: 10, height: 10, cursor: 'nesw-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('sw', e)}
                sx={{ position: 'absolute', bottom: 0, left: 0, width: 10, height: 10, cursor: 'nesw-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('se', e)}
                sx={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, cursor: 'nwse-resize', zIndex: 6 }}
              />
            </>
          )}
        </Paper>
      </Slide>

      <Slide direction="up" in={isChatOpen} mountOnEnter unmountOnExit>
        <Paper
          ref={chatRef}
          elevation={8}
          sx={{
            position: 'fixed',
            left: isMaximized ? 0 : position.x,
            top: isMaximized ? 0 : position.y,
            width: isMaximized ? '100vw' : size.width,
            height: isMinimized ? 'auto' : isMaximized ? '100vh' : size.height,
            maxWidth: isMaximized ? '100vw' : { xs: '70vw', md: '99vw' },
            maxHeight: isMinimized ? 'none' : isMaximized ? '100vh' : { xs: '70vh', md: '99vh' },
            zIndex: 1301,
            borderRadius: isMaximized ? 0 : 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
          }}
          onMouseDown={handleMouseDown}
        >
          <Box
            ref={dragRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1,
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
              cursor: 'move',
              minHeight: '48px',
              '&:hover': { backgroundColor: '#e8e8e8' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <DragIcon sx={{ color: '#666666', fontSize: 18, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ConnectionStatus isConnected={false} error={connectionError} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={() => setIsMinimized(!isMinimized)}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#000000' } }}
              >
                {isMinimized ? '□' : '—'}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggleMaximize}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: '#000000' } }}
              >
                {isMaximized ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleToggleOne}
                sx={{ color: '#666666', padding: '4px', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {!isMinimized && (
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                backgroundColor: '#efeae2',
              }}
            >
              {sessionLoading && !sessionId ? (
                <Box sx={{ p: 2, textAlign: 'center', color: '#666666' }}>Creating chat session...</Box>
              ) : (
                <>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      textAlign: 'center',
                      backgroundColor: '#efeae2',
                      color: '#555',
                      fontSize: '14px',
                      fontWeight: 500,
                      position: 'relative',
                    }}
                  >
                    <Box
                      component="img"
                      src="/LX_Genie_02 (1).gif" // your SVG file
                      alt="animation"
                      sx={{
                        position: 'absolute',
                        width: '60%',
                        opacity: 0.2, // subtle effect
                        zIndex: 1,
                        pointerEvents: 'none',
                      }}
                    />

                    <Box sx={{ position: 'relative', zIndex: 2, color: '#555' }}>
                      <span style={{ fontSize: '16px', fontWeight: 600 }}>🚀 Rolling out soon</span>
                      <span style={{ fontSize: '12px', marginTop: '6px', color: '#888', display: 'block' }}>
                        This feature will be available shortly
                      </span>
                    </Box>
                  </Box>
                  <MessageInput onSendMessage={handleSendMessage} disabled={true} />
                </>
              )}
            </Box>
          )}

          {!isMinimized && !isMaximized && (
            <>
              <Box
                onMouseDown={(e) => handleResizeMouseDown('n', e)}
                sx={{ position: 'absolute', top: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('s', e)}
                sx={{ position: 'absolute', bottom: 0, left: 8, right: 8, height: 6, cursor: 'ns-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('w', e)}
                sx={{ position: 'absolute', top: 8, bottom: 8, left: 0, width: 6, cursor: 'ew-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('e', e)}
                sx={{ position: 'absolute', top: 8, bottom: 8, right: 0, width: 6, cursor: 'ew-resize', zIndex: 5 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('nw', e)}
                sx={{ position: 'absolute', top: 0, left: 0, width: 10, height: 10, cursor: 'nwse-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('ne', e)}
                sx={{ position: 'absolute', top: 0, right: 0, width: 10, height: 10, cursor: 'nesw-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('sw', e)}
                sx={{ position: 'absolute', bottom: 0, left: 0, width: 10, height: 10, cursor: 'nesw-resize', zIndex: 6 }}
              />
              <Box
                onMouseDown={(e) => handleResizeMouseDown('se', e)}
                sx={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, cursor: 'nwse-resize', zIndex: 6 }}
              />
            </>
          )}
        </Paper>
      </Slide>

      <ConsentConfirmationModal
        open={aiConsentModalOpen}
        onClose={closeAiConsentModal}
        pendingAiConsentRef={pendingAiConsentRef}
        onAccepted={openWidgetAfterConsent}
        onError={(message) => showWarningSnackbar(message)}
      />
    </>
  );
}
